import { AbstractAgent } from "@ag-ui/client";
import {
  FrontendToolHandlerContext,
  CopilotKitCore,
  CopilotKitCoreRuntimeConnectionStatus,
  CopilotRuntimeTransport,
} from "@copilotkit/core";
import { Injectable, Injector, Signal, WritableSignal, runInInjectionContext, signal, inject } from "@angular/core";
import { FrontendToolConfig, RenderToolCallConfig } from "./tools";
import { injectAgentKitConfig } from "./agent-kit-config";

@Injectable({ providedIn: "root" })
export class AgentKitService {
  readonly #config = injectAgentKitConfig();

  readonly #rootInjector = inject(Injector);

  readonly #agents = signal<Record<string, AbstractAgent>>(this.#config.agents ?? {});

  readonly agents = this.#agents.asReadonly();

  readonly #runtimeConnectionStatus = signal<CopilotKitCoreRuntimeConnectionStatus>(
    CopilotKitCoreRuntimeConnectionStatus.Disconnected,
  );

  readonly runtimeConnectionStatus = this.#runtimeConnectionStatus.asReadonly();

  readonly #runtimeUrl = signal<string | undefined>(undefined);

  readonly runtimeUrl = this.#runtimeUrl.asReadonly();

  readonly config = this.#config;

  readonly #runtimeTransport = signal<CopilotRuntimeTransport>("rest");

  readonly runtimeTransport = this.#runtimeTransport.asReadonly();

  readonly #headers = signal<Record<string, string>>({});

  readonly headers = this.#headers.asReadonly();

  readonly core = new CopilotKitCore({
    runtimeUrl: this.#config.runtimeUrl,
    headers: this.#config.headers,
    properties: {
      streamMode: ["events", "custom"],
      streamSubgraphs: true,
      ...this.#config.properties,
    },
    agents__unsafe_dev_only: {
      ...this.#config.agents,
      ...this.#config.selfManagedAgents,
    },
    tools: this.#config.tools,
    debug: this.#config.debug,
  });

  readonly #toolCallRenderConfigs: WritableSignal<RenderToolCallConfig[]> = signal([]);

  readonly #clientToolCallRenderConfigs: WritableSignal<FrontendToolConfig[]> = signal([]);

  readonly toolCallRenderConfigs: Signal<RenderToolCallConfig[]> = this.#toolCallRenderConfigs.asReadonly();

  readonly clientToolCallRenderConfigs: Signal<FrontendToolConfig[]> = this.#clientToolCallRenderConfigs.asReadonly();

  constructor() {
    this.#runtimeConnectionStatus.set(this.core.runtimeConnectionStatus);
    this.#runtimeUrl.set(this.core.runtimeUrl);
    this.#headers.set(this.core.headers);

    if (this.#config.runtimeTransport) {
      this.core.setRuntimeTransport(this.#config.runtimeTransport);
      this.#runtimeTransport.set(this.#config.runtimeTransport);
    } else {
      this.#runtimeTransport.set(this.core.runtimeTransport);
    }

    this.#config.renderToolCalls?.forEach((renderConfig) => {
      this.addRenderToolCall(renderConfig);
    });

    this.#config.tools?.forEach((tool) => {
      if (tool.renderer && tool.parameters) {
        this.addRenderToolCall({
          name: tool.name,
          args: tool.parameters,
          component: tool.renderer,
          agentId: tool.agentId,
        });
      }
    });

    this.#config.frontendTools?.forEach((clientTool) => {
      this.addFrontendTool({ ...clientTool, injector: this.#rootInjector });
    });

    this.core.subscribe({
      onAgentsChanged: () => {
        this.#agents.set(this.core.agents);
      },
      onRuntimeConnectionStatusChanged: ({ status }) => {
        this.#runtimeConnectionStatus.set(status);
      },
      onHeadersChanged: ({ headers }) => {
        this.#headers.set(headers);
      },
    });
  }

  /**
   * Registers a frontend tool for execution via the LangGraph interrupt(type=frontend_tools) path.
   *
   * Tools are NOT registered with core.addTool() to avoid double-execution: CopilotKit's
   * processAgentResult would auto-execute the tool AND executeAndResumeFrontendTools would also
   * execute it, causing two simultaneous runAgent calls and a "Thread already running" error.
   * callFrontendTool() resolves handlers from this registry directly.
   */
  addFrontendTool(
    clientToolWithInjector: FrontendToolConfig & {
      injector: Injector;
    },
  ): void {
    const alreadyRegistered = this.#clientToolCallRenderConfigs().some(
      (c) => c.name === clientToolWithInjector.name && c.agentId === clientToolWithInjector.agentId,
    );
    if (alreadyRegistered) {
      return;
    }
    this.#clientToolCallRenderConfigs.update((current) => [...current, clientToolWithInjector]);
  }

  addRenderToolCall(renderConfig: RenderToolCallConfig): void {
    this.#toolCallRenderConfigs.update((current) => [...current, renderConfig]);
  }

  #isSameAgentId<T extends { agentId?: string }>(target: T, agentId?: string): boolean {
    if (agentId) {
      return target.agentId === agentId;
    }
    return true;
  }

  removeTool(toolName: string, agentId?: string): void {
    const keep = (config: { name: string; agentId?: string }) =>
      config.name !== toolName || (agentId === undefined ? !!config.agentId : !this.#isSameAgentId(config, agentId));
    this.#clientToolCallRenderConfigs.update((current) => current.filter(keep));
    this.#toolCallRenderConfigs.update((current) => current.filter(keep));
  }

  getAgent(agentId: string): AbstractAgent | undefined {
    return this.core.getAgent(agentId);
  }

  /**
   * Executes a registered frontend tool by name with the given args.
   * Called by executeAndResumeFrontendTools when the backend sends a frontend_tools interrupt.
   */
  async callFrontendTool(name: string, args: Record<string, unknown>, agent: AbstractAgent): Promise<unknown> {
    const configs = this.#clientToolCallRenderConfigs() as Array<FrontendToolConfig & { injector?: Injector }>;
    const config = configs.find((c) => c.name === name);
    if (!config) {
      throw new Error(`Frontend tool "${name}" is not registered`);
    }
    const context: FrontendToolHandlerContext = {
      toolCall: { id: name, type: "function", function: { name, arguments: JSON.stringify(args) } },
      agent,
    };
    return runInInjectionContext(config.injector ?? this.#rootInjector, () => config.handler(args, context)).then(
      (result) => {
        return result;
      },
      (err) => {
        console.error(`[FrontendTool] HANDLER ERROR: "${name}"`, err);
        throw err;
      },
    );
  }

  updateRuntime(options: {
    runtimeUrl?: string;
    runtimeTransport?: CopilotRuntimeTransport;
    headers?: Record<string, string>;
    properties?: Record<string, unknown>;
    agents?: Record<string, AbstractAgent>;
    selfManagedAgents?: Record<string, AbstractAgent>;
  }): void {
    if (options.runtimeUrl !== undefined) {
      this.core.setRuntimeUrl(options.runtimeUrl);
      this.#runtimeUrl.set(options.runtimeUrl);
    }
    if (options.runtimeTransport !== undefined) {
      this.core.setRuntimeTransport(options.runtimeTransport);
      this.#runtimeTransport.set(options.runtimeTransport);
    }
    if (options.headers !== undefined) {
      this.core.setHeaders(options.headers);
      this.#headers.set(options.headers);
    }
    if (options.properties !== undefined) {
      this.core.setProperties(options.properties);
    }
    if (options.agents !== undefined || options.selfManagedAgents !== undefined) {
      this.core.setAgents__unsafe_dev_only({
        ...(options.agents ?? this.#config.agents),
        ...(options.selfManagedAgents ?? this.#config.selfManagedAgents),
      });
    }
  }

  #mapContextId: string | undefined;

  /**
   * Replaces the map context entry with the latest map state.
   * Removes the previous entry first so only one map-context record exists at any time.
   * Arrives at the backend as config.configurable.context[n].value (JSON string).
   */
  updateMapContext(mapContext: unknown): void {
    if (!mapContext) return;
    if (this.#mapContextId) {
      this.core.removeContext(this.#mapContextId);
    }
    this.#mapContextId = this.core.addContext({
      value: JSON.stringify(mapContext),
      description:
        "Current map view: center (lat/lng), bbox ([minLon,minLat,maxLon,maxLat]), zoom, availableCollections [{id,name,layer,oriented,product_type,year}], selectedCollection {id,name,layer,oriented,product_type,year}, overlayLayers [{id,name,file_type,childs?}] — childs is present only on GROUPLAYER entries and holds the same {id,name,file_type,childs?} shape recursively for each child layer",
    });
  }
}
